if (!customElements.get('storefront-slider')) {
  customElements.define(
    'storefront-slider',
    class StorefrontSlider extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        this.slides = Array.from(this.querySelectorAll('[data-storefront-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-storefront-dot]'));
        this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
        this.autoplayEnabled = this.dataset.autoplay === 'true' && this.slides.length > 1;
        this.interval = Math.max(3000, Number(this.dataset.interval) || 5000);
        this.reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.timer = null;
        this.pointerStartX = null;

        this.show(this.currentIndex);
        if (this.slides.length < 2) return;

        const signal = this.controller.signal;
        this.querySelector('[data-storefront-prev]')?.addEventListener('click', () => this.interact(-1), { signal });
        this.querySelector('[data-storefront-next]')?.addEventListener('click', () => this.interact(1), { signal });
        this.dots.forEach((dot) => {
          dot.addEventListener(
            'click',
            () => {
              this.pause();
              this.show(Number(dot.dataset.storefrontDot), true);
            },
            { signal }
          );
        });

        this.addEventListener('mouseenter', () => this.stopTimer(), { signal });
        this.addEventListener('mouseleave', () => this.startTimer(), { signal });
        this.addEventListener('focusin', () => this.stopTimer(), { signal });
        this.addEventListener(
          'focusout',
          (event) => {
            if (!this.contains(event.relatedTarget)) this.startTimer();
          },
          { signal }
        );
        this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
        this.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal, passive: true });
        this.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal, passive: true });
        this.addEventListener('pointercancel', () => (this.pointerStartX = null), { signal });

        this.handleVisibility = () => (document.hidden ? this.stopTimer() : this.startTimer());
        this.handleBlockSelect = (event) => {
          const slide = event.target.closest('[data-storefront-slide]');
          if (!slide || !this.contains(slide)) return;
          this.pause();
          this.show(Number(slide.dataset.slideIndex));
        };
        document.addEventListener('visibilitychange', this.handleVisibility, { signal });
        document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });
        this.startTimer();
      }

      disconnectedCallback() {
        this.stopTimer();
        this.controller?.abort();
      }

      interact(step) {
        this.pause();
        this.show(this.currentIndex + step, true);
      }

      onKeydown(event) {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (event.target.closest('input, textarea, select')) return;

        event.preventDefault();
        const isRtl = this.dataset.rtl === 'true';
        const nextStep = event.key === 'ArrowRight' ? 1 : -1;
        this.interact(isRtl ? -nextStep : nextStep);
      }

      onPointerDown(event) {
        if (event.pointerType === 'mouse' || !event.isPrimary) return;
        this.pointerStartX = event.clientX;
        this.pause();
      }

      onPointerUp(event) {
        if (this.pointerStartX === null || !event.isPrimary) return;
        const distance = event.clientX - this.pointerStartX;
        this.pointerStartX = null;
        if (Math.abs(distance) < 48) return;

        const isRtl = this.dataset.rtl === 'true';
        const nextStep = distance < 0 ? 1 : -1;
        this.show(this.currentIndex + (isRtl ? -nextStep : nextStep), true);
      }

      show(index, focusSlide = false) {
        if (!this.slides.length) return;
        const nextIndex = (index + this.slides.length) % this.slides.length;

        this.slides.forEach((slide, slideIndex) => {
          const active = slideIndex === nextIndex;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
          slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
            if (active) {
              element.removeAttribute('tabindex');
            } else {
              element.setAttribute('tabindex', '-1');
            }
          });
        });

        this.dots.forEach((dot, dotIndex) => {
          const active = dotIndex === nextIndex;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', String(active));
        });

        this.currentIndex = nextIndex;
        if (focusSlide) this.slides[nextIndex].focus({ preventScroll: true });
      }

      startTimer() {
        if (!this.autoplayEnabled || this.reduceMotionQuery.matches || document.hidden || this.timer) return;
        this.timer = window.setTimeout(() => {
          this.timer = null;
          this.show(this.currentIndex + 1);
          this.startTimer();
        }, this.interval);
      }

      stopTimer() {
        if (!this.timer) return;
        window.clearTimeout(this.timer);
        this.timer = null;
      }

      pause() {
        this.autoplayEnabled = false;
        this.stopTimer();
      }
    }
  );
}
